// TEMPORARY web33 experiment. Teardown: delete this folder and remove its
// App.tsx, chart, analytics, debug, style, and version call sites.

import { ChartData } from "../../charts/Chart";

// Candidate projection shapes. Each is a list of progress fractions of the
// total signed range (0 = today's value, 1 = the goal), so one profile works
// for both descending (weight/body-fat loss) and ascending (muscle) charts.
// The vocabulary is the same one the opening arc teaches: the expectation
// question's three thumbnails (steady / big-results-early / ups-and-downs)
// and the reveal splash's winding path.
//
// QA: append ?bend=<key> to compare them on a real chart.
const BEND_PROFILES = {
  // --- Early-progress shapes (kept from the first round) -------------------
  frontloaded: [0, 0.32, 0.55, 0.7, 0.8, 0.87, 0.93, 0.97, 1],
  lateSurge: [0, 0.03, 0.09, 0.17, 0.28, 0.42, 0.6, 0.79, 1],

  // --- S-curve family: settle in, productive middle, ease into the goal ----
  sCurve: [0, 0.03, 0.11, 0.28, 0.5, 0.71, 0.87, 0.96, 1],
  // Shallower S — never looks stalled at either end.
  sCurveSoft: [0, 0.07, 0.18, 0.33, 0.5, 0.67, 0.82, 0.93, 1],
  // Longer settling period, then a decisive middle.
  sCurveLate: [0, 0.02, 0.06, 0.17, 0.4, 0.67, 0.87, 0.96, 1],

  // --- Staircase family: push, hold, push, hold (never backslides) ---------
  staircase: [0, 0.04, 0.22, 0.24, 0.26, 0.48, 0.5, 0.52, 0.74, 0.76, 0.78, 0.95, 1],
  // Softer treads: shorter holds, so the steps read as rhythm not stalls.
  staircaseSoft: [0, 0.09, 0.25, 0.29, 0.33, 0.5, 0.54, 0.58, 0.76, 0.8, 0.84, 0.95, 1],
  // Tilted holds: the "flat" stretches still drift toward the goal, so a
  // plateau reads as slow progress rather than a full stop.
  staircaseTilted: [0, 0.05, 0.24, 0.28, 0.32, 0.5, 0.54, 0.58, 0.76, 0.8, 0.84, 0.96, 1],

  // --- Undulating family: one line, breathing slope ------------------------
  undulating: [0, 0.16, 0.22, 0.4, 0.46, 0.63, 0.69, 0.85, 1],
  // Deeper swells — the fast/slow contrast is obvious at a glance.
  undulatingDeep: [0, 0.2, 0.25, 0.46, 0.51, 0.7, 0.75, 0.89, 1],
  // Adds two barely-there backtracks (~2% of range) on the way down.
  undulatingDip: [0, 0.18, 0.26, 0.24, 0.46, 0.55, 0.53, 0.77, 1],
} as const;

type BendProfileKey = keyof typeof BEND_PROFILES;

const DEFAULT_BEND_PROFILE: BendProfileKey = "undulating";

const isBendProfileKey = (value: string | null): value is BendProfileKey =>
  value !== null && value in BEND_PROFILES;

// QA override only: ?bend=<key>. The app rewrites the URL to /page/N before
// the chart mounts, so the choice is persisted on first read and honoured for
// the rest of the session (clear with ?bend=default).
const BEND_OVERRIDE_KEY = "web33_bend_profile";

export function resolveBendProfile(): BendProfileKey {
  try {
    const requested = new URLSearchParams(window.location.search).get("bend");
    if (requested === "default") {
      localStorage.removeItem(BEND_OVERRIDE_KEY);
      return DEFAULT_BEND_PROFILE;
    }
    if (isBendProfileKey(requested)) {
      localStorage.setItem(BEND_OVERRIDE_KEY, requested);
      return requested;
    }
    const stored = localStorage.getItem(BEND_OVERRIDE_KEY);
    if (isBendProfileKey(stored)) return stored;
  } catch {
    // no window/localStorage (tests) — use the default
  }
  return DEFAULT_BEND_PROFILE;
}

export function bendChartData(
  data: ChartData[],
  profileKey: BendProfileKey = resolveBendProfile(),
): ChartData[] {
  if (data.length < 2) return data.slice();

  const first = data[0] as ChartData & Record<string, unknown>;
  const last = data[data.length - 1] as ChartData & Record<string, unknown>;
  const valueKey = Object.keys(first).find(
    (key) => key !== "date" && typeof first[key] === "number" && typeof last[key] === "number",
  );
  if (!valueKey) return data.slice();

  const start = first[valueKey] as number;
  const end = last[valueKey] as number;
  const range = end - start;
  if (range === 0) return data.slice();

  const profile = BEND_PROFILES[profileKey];
  const startMillis = first.date.toMillis();
  const endMillis = last.date.toMillis();
  const dataMin = Math.min(start, end);
  const dataMax = Math.max(start, end);

  return profile.map((progress, index) => {
    if (index === 0) return data[0];
    if (index === profile.length - 1) return data[data.length - 1];

    const timeProgress = index / (profile.length - 1);
    const value = Math.min(dataMax, Math.max(dataMin, start + range * progress));

    return {
      ...first,
      date: first.date.plus({
        milliseconds: (endMillis - startMillis) * timeProgress,
      }),
      [valueKey]: value,
    };
  });
}

export { BEND_PROFILES, DEFAULT_BEND_PROFILE };
export type { BendProfileKey };
